import React, { useState } from 'react';
import classnames from 'classnames';
import { ReactComponent as CheckmarkIcon } from '$/assets/icons/checkmark.svg';
import { ReactComponent as PencilIcon } from '$/assets/icons/penciledit.svg';
import { ReactComponent as XIcon } from '$/assets/icons/x-icon.svg';
import { ReactComponent as WarningIcon } from '$/assets/icons/exclamation-triangle.svg';
import { ReactComponent as DownloadIcon } from '$/assets/icons/download.svg';
import Modal from '$/components/global/modal';
import InputBlock from '$/components/global/inputBlock';
import * as Bixby from '$/core';
import './index.scss';
import { CompanyDocumentCompanyDocumentCategory, IDocumentFile } from '$/core/models';
import CompanyDocumentsCategoryPicker from '$/components/company-documents-category-picker';
import CompanyDocumentsCategoryEditor from '$/components/company-documents-category-editor';

export interface IFileListProps {
  allowDocumentSharing: boolean;
  files: Bixby.Models.IDocumentFile[];
  companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[];
  fiscalYearEnd?: Date | null;
  onDownloadDocument: (id: string, index?: number) => void;
  onDownloadWatermarkRemovedDocument: (filePath: string) => void;
  onDeleteFile: (id: string) => void;
  onSetMetadata: (id: string, field: keyof Bixby.Models.IDocumentFile, value: any) => void;
  onSetAllUploadClientSpecific: (value: boolean) => void;
  dispatchToastError: (message: string) => Promise<void>;
}

function StatusCell({ hasError, message, hasValue }: { message?: string; hasError?: boolean; hasValue?: boolean }) {
  let inner: JSX.Element;
  let iconIsError = hasError;

  if (hasError === undefined) {
    inner = <span>Not Run</span>;
  } else {
    inner = hasError ? <XIcon className="error" /> : <CheckmarkIcon className="checkmark-icon" />;
  }

  let friendlyMessage: string | undefined = undefined;
  switch (message) {
    case Bixby.Constants.DOCUMENT_HOSTING_CLIENT_REQUIRED:
      friendlyMessage = 'Client required';
      inner = <XIcon className="error" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS:
      friendlyMessage = 'Watermarked document already exists';
      inner = <WarningIcon className="warning" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS:
      friendlyMessage = 'Non watermarked document already exists';
      inner = <WarningIcon className="warning" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_MATCH_NOT_FOUND:
      friendlyMessage = 'Match not found for document name, extension, and date posted';
      inner = <XIcon className="error" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND:
      friendlyMessage = 'Multiple matching file names found in Excel file';
      inner = <XIcon className="error" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_INVALID_ROW_NUMBER:
      friendlyMessage = "Data in provided row didn't match file submitted by client";
      inner = <XIcon className="error" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_INVALID_DECLARATION_TYPE:
      friendlyMessage = 'Invalid document declaration type';
      inner = <XIcon className="error" />;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_WATERMARK_FROM_DATA_SITE:
      friendlyMessage = hasValue ? 'Watermarked From Data Site' : '';
      inner = hasValue === undefined ? <></> : hasValue ? <span>Yes</span> : <span>No</span>;
      break;
    case Bixby.Constants.DOCUMENT_HOSTING_WATERMARK_REMOVED:
      friendlyMessage = hasValue ? 'Watermark Removed Successfully' : '';
      inner =
        hasValue === undefined ? (
          <></>
        ) : hasValue ? (
          <CheckmarkIcon className="checkmark-icon" />
        ) : (
          <XIcon className="error" />
        );
      // Failure state must use has-error so the XIcon renders at the same size as other error icons.
      if (hasValue === false) {
        iconIsError = true;
      }
      break;
    default:
      break;
  }

  return (
    <div
      title={friendlyMessage}
      className={classnames('file-validation', { 'has-success': !iconIsError, 'has-error': !!iconIsError })}>
      {inner}
    </div>
  );
}

function EditCell({
  editingId,
  id,
  value,
  isEditable,
  setIsEditingId,
  setValue,
  onSave,
  onCancel,
}: {
  editingId: string | undefined;
  id: string;
  value: string | undefined;
  isEditable: boolean;
  setIsEditingId: (value: string) => void;
  setValue: (value: string) => void;
  onSave: (id: string) => void;
  onCancel: () => void;
}) {
  if (!isEditable) {
    return <div>{value}</div>;
  }
  return (
    <>
      {editingId !== id && (
        <div className="edit-contain">
          <div>{value || 'Add'}</div>
          <div />
          <button onClick={() => setIsEditingId(id)}>
            <PencilIcon role="presentation" />
          </button>
        </div>
      )}
      {editingId === id && (
        <div className="edit-contain">
          <InputBlock title="">
            <input type="text" defaultValue={value} onChange={e => setValue(e.target.value)} />
          </InputBlock>
          <button onClick={() => onSave(id)}>
            <CheckmarkIcon className="checkmark-icon" role="presentation" />
          </button>
          <button onClick={onCancel}>
            <XIcon className="x-icon" role="presentation" />
          </button>
        </div>
      )}
    </>
  );
}

type PartialDocument = Pick<Bixby.Models.IDocumentFile, 'id' | 'companyDocumentCompanyDocumentCategories'>;

const FileList: React.FC<IFileListProps> = ({
  files,
  onDeleteFile,
  companyDocumentCategories,
  allowDocumentSharing,
  onSetMetadata,
  onSetAllUploadClientSpecific,
  onDownloadDocument,
  onDownloadWatermarkRemovedDocument,
  fiscalYearEnd,
  dispatchToastError,
}) => {
  const [editingCategory, setEditingCategory] = useState(undefined as PartialDocument | undefined);
  const [editingTitle, setEditingTitle] = useState<string | undefined>();
  const [title, setTitle] = useState<string | undefined>();
  const [, setCategories] = useState<Bixby.Models.CompanyDocumentCompanyDocumentCategory[] | undefined>();
  const [editingDescription, setEditingDescription] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [rowNumber, setRowNumber] = useState<string | undefined>();
  const [editingRowNumber, setEditingRowNumber] = useState<string | undefined>();

  const openModal = (id: string) => {
    const file = files.find(f => f.id === id) as PartialDocument;
    setEditingCategory(file);
  };

  const handleCategoryModalClose = () => {
    setEditingCategory(undefined);
  };

  const handleSaveCategory = (id: string, allCategories: CompanyDocumentCompanyDocumentCategory[]) => {
    onSetMetadata(id, 'companyDocumentCompanyDocumentCategories', allCategories);
    setCategories(allCategories);
    handleCategoryModalClose();
  };

  const handleSaveTitle = (id: string) => {
    onSetMetadata(id, 'title', title);
    setEditingTitle(undefined);
    setTitle(undefined);
  };

  const handleSaveDescription = (id: string) => {
    onSetMetadata(id, 'description', description);
    setEditingDescription(undefined);
    setDescription(undefined);
  };

  const handleSaveRowNumber = (id: string) => {
    onSetMetadata(id, 'rowNumber', rowNumber);
    setEditingRowNumber(undefined);
    setRowNumber(undefined);
  };

  const handleCancelTitle = () => {
    setEditingTitle(undefined);
    setTitle(undefined);
  };

  const handleCancelDescription = () => {
    setEditingDescription(undefined);
    setDescription(undefined);
  };

  const handleCancelRowNumber = () => {
    setEditingRowNumber(undefined);
    setRowNumber(undefined);
  };

  const handleDownloadDocument = async (id: string, index?: number) => {
    await onDownloadDocument(id, index);
  };

  const handleDownloadWatermarkRemovedDocument = async (filePath?: string) => {
    if (filePath) {
      await onDownloadWatermarkRemovedDocument(filePath);
    } else {
      dispatchToastError('Invalid File Path');
    }
  };

  const handleOverrideChecked = async (file: IDocumentFile) => {
    onSetMetadata(file.id, 'overrideAlreadyExistsError', true);
    calculateEditableState(file.error, true);
  };
  const handleOverrideUnchecked = async (file: IDocumentFile) => {
    onSetMetadata(file.id, 'overrideAlreadyExistsError', false);
    calculateEditableState(file.error, false);
  };
  const calculateEditableState = (error?: string | undefined, overrideAlreadyExistsError?: boolean) => {
    if (!error) {
      return true;
    }
    const hasAlreadyExistsError =
      error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
      error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
    if (hasAlreadyExistsError && overrideAlreadyExistsError) {
      return true;
    }
    const passesError = !hasAlreadyExistsError && error !== Bixby.Constants.DOCUMENT_HOSTING_MATCH_NOT_FOUND;
    return passesError;
  };
  const fileListHasAlreadyExistsError = files.some(_ => _.requireAlreadyExistsOverride || _.overrideAlreadyExistsError);

  const allClientSpecific = allowDocumentSharing && files.length > 0 && files.every(f => f.uploadClientSpecific);
  const someClientSpecific = allowDocumentSharing && files.some(f => f.uploadClientSpecific) && !allClientSpecific;

  const selectAllRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someClientSpecific;
    }
  }, [someClientSpecific]);

  return (
    <>
      <div
        className={classnames('file-list', {
          'has-ten ': !allowDocumentSharing && !fileListHasAlreadyExistsError,
          'has-eleven ': !allowDocumentSharing && fileListHasAlreadyExistsError,
          'has-fourteen ': allowDocumentSharing && !fileListHasAlreadyExistsError,
          'has-fifteen': allowDocumentSharing && fileListHasAlreadyExistsError,
        })}>
        {' '}
        <Header
          allowDocumentSharing={allowDocumentSharing}
          fileListHasAlreadyExistsError={fileListHasAlreadyExistsError}
          selectAllRef={selectAllRef}
          allClientSpecific={allClientSpecific}
          onSetAllUploadClientSpecific={onSetAllUploadClientSpecific}
        />
        {files.map((file, ix) => {
          return (
            <React.Fragment key={ix}>
              <EditCell
                editingId={editingRowNumber}
                id={file.id}
                value={file.rowNumber ? file.rowNumber.toString() : undefined}
                isEditable={calculateEditableState(file.error, file.overrideAlreadyExistsError)}
                setIsEditingId={setEditingRowNumber}
                setValue={setRowNumber}
                onSave={handleSaveRowNumber}
                onCancel={handleCancelRowNumber}
              />
              <div className="wrapped" title={file.fileName || file.file.name}>
                {file.fileName || file.file.name}
              </div>
              <EditCell
                editingId={editingTitle}
                id={file.id}
                value={file.title}
                isEditable={calculateEditableState(file.error, file.overrideAlreadyExistsError)}
                setIsEditingId={setEditingTitle}
                setValue={setTitle}
                onSave={handleSaveTitle}
                onCancel={handleCancelTitle}
              />
              <EditCell
                editingId={editingDescription}
                id={file.id}
                value={file.description}
                isEditable={calculateEditableState(file.error, file.overrideAlreadyExistsError)}
                setIsEditingId={setEditingDescription}
                setValue={setDescription}
                onSave={handleSaveDescription}
                onCancel={handleCancelDescription}
              />
              <CompanyDocumentsCategoryEditor
                documentId={file.id}
                companyDocumentCompanyDocumentCategories={file.companyDocumentCompanyDocumentCategories}
                isEditable={calculateEditableState(file.error, file.overrideAlreadyExistsError)}
                onEditCategory={openModal}
                onDeleteCategory={newCategories => handleSaveCategory(file.id, newCategories || [])}
              />
              <div>{file.documentDeclarationType}</div>
              <StatusCell hasError={file.hasDuplicateNameValidationError} />
              <StatusCell hasError={file.hasServerValidationError} message={file.error} />
              {allowDocumentSharing && (
                <>
                  <StatusCell
                    hasValue={file.isWatermarked}
                    message={Bixby.Constants.DOCUMENT_HOSTING_WATERMARK_FROM_DATA_SITE}
                  />
                  <StatusCell
                    hasValue={file.isWatermarkRemoved}
                    message={Bixby.Constants.DOCUMENT_HOSTING_WATERMARK_REMOVED}
                  />
                  {file.isWatermarkRemoved ? (
                    <button
                      title="Download Watermark Removed File"
                      className="download-button"
                      onClick={e => {
                        e.preventDefault();
                        handleDownloadWatermarkRemovedDocument(file.watermarkRemovedBlobFilePath);
                      }}>
                      <DownloadIcon />
                    </button>
                  ) : (
                    <div></div>
                  )}
                  <button
                    title="Download"
                    className="download-button"
                    onClick={e => {
                      e.preventDefault();
                      handleDownloadDocument(file.id, file.index);
                    }}>
                    <DownloadIcon />
                  </button>
                  <div className="upload-client-specific">
                    <input
                      type="checkbox"
                      checked={file.uploadClientSpecific ?? false}
                      onChange={e => onSetMetadata(file.id, 'uploadClientSpecific', e.target.checked)}
                    />
                  </div>
                </>
              )}
              {fileListHasAlreadyExistsError && (
                <div className="override">
                  <input
                    type="checkbox"
                    disabled={!file.requireAlreadyExistsOverride}
                    checked={file.overrideAlreadyExistsError}
                    onChange={e => (e.target.checked ? handleOverrideChecked(file) : handleOverrideUnchecked(file))}
                  />
                </div>
              )}
              {!allowDocumentSharing && (
                <button
                  title="Download"
                  className="download-button"
                  onClick={e => {
                    e.preventDefault();
                    handleDownloadDocument(file.id, file.index);
                  }}>
                  <DownloadIcon />
                </button>
              )}
              <button className="fa fa-trash" onClick={() => onDeleteFile(file.id)} />
            </React.Fragment>
          );
        })}
      </div>
      <Modal active={!!editingCategory} onClose={handleCategoryModalClose}>
        <div className="modal_content">
          {editingCategory && (
            <CompanyDocumentsCategoryPicker
              id={editingCategory.id}
              allCategories={companyDocumentCategories}
              currentCompanyDocumentCategories={editingCategory.companyDocumentCompanyDocumentCategories}
              onSave={handleSaveCategory}
              onCancel={handleCategoryModalClose}
              fiscalYearEnd={fiscalYearEnd}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default FileList;

const Header = ({
  allowDocumentSharing,
  fileListHasAlreadyExistsError,
  selectAllRef,
  allClientSpecific,
  onSetAllUploadClientSpecific,
}: {
  allowDocumentSharing: boolean;
  fileListHasAlreadyExistsError: boolean;
  selectAllRef?: React.RefObject<HTMLInputElement>;
  allClientSpecific?: boolean;
  onSetAllUploadClientSpecific?: (value: boolean) => void;
}) => {
  return (
    <>
      <div>Row</div>
      <div>File Name</div>
      <div>Title</div>
      <div>Description</div>
      <div>Category</div>
      <div>Declaration</div>
      <div>Unique?</div>
      <div>Valid?</div>
      {allowDocumentSharing && (
        <>
          <div>Watermarked from Data Site?</div>
          <div>Watermark Removed</div>
          <div>Clean Document</div>
          <div>Original Document</div>
          <div className="upload-client-specific-header">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={allClientSpecific ?? false}
              title="Select All"
              onChange={e => onSetAllUploadClientSpecific && onSetAllUploadClientSpecific(e.target.checked)}
            />
            <span>Upload Client Specific</span>
          </div>
        </>
      )}
      {fileListHasAlreadyExistsError && <div>Override</div>}
      <div />
      {!allowDocumentSharing && <div />}
    </>
  );
};
